import React from 'react';
import { CrmProvider, ECrmFieldEditAccess } from 'types/crm';

export const L10nSettings = /** @type {const} */ ({
  teamAddError: 'Unable to add user to team. Please try again later.',
  teamRemoveError: 'Unable to remove user from team. Please try again later.',
  noTeamsFound: 'No teams found',
  zoomSettingNoAccess:
    'You do not have access to modify the settings in Zoom, get in touch with your Zoom admins to modify the same.',
  crmFAQs: 'CRM FAQs',
  recordingAndConferencing: {
    cloudRecordingDisabled:
      'Your conversations will be recorded by Avoma Assistant (Bot) because cloud recording is disabled by your admin.',
    auditTrail: {
      lastUpdatedBy: 'Last updated by',
      on: 'on'
    },
    saveAndApplySettings: 'Save & Apply settings',
    avomaBot: 'Avoma Assistant (Bot)',
    successMsg: 'Settings saved successfully.',
    orgPoliciesSuccessMsg: 'Settings saved successfully, users notified.',
    errorMsg: 'Something went wrong. Please try again later.',
    forBasicFreeLicense: 'For "Basic" (Free) license holders',
    forPaidLicense: 'For "Paid" license holders',
    connected: 'connected',
    connectedViaAdmin: 'connected via admin',
    adminConnectedConfigPending: 'Admin connected — configuration pending',
    cloudRecordingSetupPending:
      'The setup for cloud recording at the workspace level is pending. Please contact your admin.',
    googleMeet: 'Google Meet',
    goToMeeting: 'GoTo Meeting',
    microsoftTeams: 'Microsoft Teams',
    willRecordYourConversation: 'will record your conversations.',
    recordingSettingsDisclaimer: {
      avoma: 'Avoma',
      will: 'will',
      notRecord: 'not record',
      settingOff: 'as recording settings are turned off.'
    },
    zoomCard: {
      zoomHelpCardTitle: 'Using Zoom Conferencing with Avoma',
      zoomCloud: 'Zoom Cloud Recording',
      avomaZoomApp: 'Avoma Zoom App',
      helperText1:
        'Using Zoom Cloud recording you can avoid Avoma Assistant (Bot) to be joining to record the meetings.',
      helperText2:
        'Install Avoma Zoom App for in-app collaborative note-taking experience.',
      helperText3:
        'Take notes and bookmark important moments with your team during the meeting from the Zoom desktop client.',
      recordVia: 'Record via',
      cloud: 'Zoom Cloud',
      install: 'Install',
      avZoomApp: 'Avoma Zoom App',
      zoomClient: 'to take notes in Zoom Client ?',
      zoomCloudIsOff: 'Zoom Cloud recording is turned OFF.',
      zoomAppInstalled: 'Avoma Zoom app installed for in-app note taking.',
      zoomAppNotInstalled: 'Avoma Zoom app is not installed.'
    },
    webExCard: {
      title: 'WebEx Conferencing',
      recordViaWebex: 'Record via WebEx Cloud'
    },
    msCloudCard: {
      title: 'Microsoft Cloud Conferencing',
      recordViaMsCloud: 'Record via Microsoft Cloud',
      askAdmin:
        'Ask your Admin to enable Microsoft Teams Cloud Recording in Avoma',
      isSupported: 'will record your conversations.'
    },
    gmCloudCard: {
      title: 'Google Meet Cloud Conferencing',
      recordViaGmeetCloud: 'Record via Google Meet Cloud',
      askAdmin: 'Ask your Admin to enable Google Meet Cloud Recording in Avoma',
      isSupported: 'will record your conversations.'
    },
    host: 'Host',
    participant: 'Participant',
    botVsCloudModal: {
      bot: 'Avoma Assistant (Bot)',
      cloud: 'Cloud Recording',
      title: 'Avoma Assistant (Bot) vs Cloud Recording',
      recordedBy: 'Recorded by',
      thirdPartyBot: '3rd party bot as participant',
      no: 'No',
      both: 'both',
      recordingAnnouncement: 'Recording announcement',
      recordsOnlyWhen: 'Records only when you are the',
      recordsWhen: 'Records only when you are the',
      cloudRecordingPoints: {
        confAppCloud: 'Conferencing app cloud',
        confAppCloudSubText:
          'Meetings will be recorded by Zoom or WebEx Cloud and they will be sent over to Avoma for processing',
        forEachParticipant: 'for each participant',
        willRecord: 'will record when you’re participant.'
      },
      avomaBotPoints: {
        andA: 'and a',
        whenBotJoins: 'when Bot joins',
        whenBotJoinsSubText:
          'i.e. Participant who join after the Bot won’t hear the recording announcement.',
        avomaBot: 'Avoma’s Bot'
      }
    },
    orgRecordingSettings: {
      leavingModal: {
        title: 'Continue without saving settings?',
        text: 'You have unsaved changes. If you proceed without saving, they will be lost forever.'
      },
      title: 'Recording settings for all users of the organization',
      bannerText:
        'If below organization recording settings are not locked then users can modify their individual recording settings.',
      toggleConfirmationModal: {
        changeWillApply:
          'This change will apply to all users in your organization and',
        all: 'ALL',
        any: 'ANY',
        not: 'NOT',
        meetingWill: 'meeting will',
        beRecorded:
          'be recorded and users will be notified via email with this change.',
        recordedWhen: 'meetings will be recorded when they are',
        informedViaEmail:
          'of the meetings and users will be notified via email with this change.',
        ifYouUndo:
          'If you UNDO this change, User’s previous recording settings will be reverted.',
        ifLocked:
          'If any of the recording settings are locked, users will not be able to make any changes on their own.'
      },
      lockConfirmationModal: {
        thisChange:
          'This change will apply to all users in your organization and',
        any: 'ANY',
        meetingWill: 'meeting will',
        not: 'NOT',
        notifiedViaEmail:
          'be recorded and users will be notified via email with this change.',
        ifLocked:
          'If any of the recording settings are locked, users will not be able to make any changes on their own.'
      },
      unLockConfirmationModal: {
        parah1:
          'You’re locking the recording preference for all users of the organization and they will not be able to modify them on their own.',
        applyToAll:
          'These preferences will apply to ALL users in your organization and',
        all: 'ALL',
        recordedWhen: 'meetings will be recorded when they are',
        notifiedViaEmail:
          'of the meetings and users will be notified via email with this change.'
      },
      acceptedToggleConfirmationModal: {
        enablingMeetingsRecorded:
          'Meetings will be recorded even if members do not accept the calendar invite',
        disablingMustAccept:
          'Members must accept calendar invite for the meeting to record, if invite is NOT accepted, the meeting will not be recorded.',
        appliesOnlyToRecorded:
          'Applies only to meetings that are supposed to be recorded.',
        ifLockedMembersCannotChange:
          'If this setting is locked, members will not be able to make any changes on their own.'
      },
      acceptedLockConfirmationModal: {
        lockMembersCannotModify:
          'Members will NOT be able to modify the setting through the member-level preference setting.',
        lockCurrentSettingAppliesToAll:
          'The current setting will apply to all members in your organization.',
        unlockMembersCanChangePreferences:
          'Members will be able to change this setting in their own preferences.',
        unlockOrgSettingNotEnforcedForEveryone:
          'The organization setting will no longer be enforced for everyone.'
      }
    },
    editUserModal: {
      banner: {
        avomaWill: 'Avoma will',
        record: 'record',
        notRecord: 'not record',
        all: "user's all",
        internalExternal: 'internal/external',
        conversations: 'conversations.',
        external: 'external',
        internal: 'internal'
      }
    },
    maleVoice: 'Male Voice',
    femaleVoice: 'Female Voice',
    goToMyZoomSeetings: 'Go to my Zoom settings'
  },
  timezone: {
    label: 'Time zone',
    description:
      "Setting your time zone helps you localize your deals and related dates to your company's location and timezone. The filters applied on date fields in deals, pipeline and forecast dashboard will use this timezone.",
    updatedSuccessfully: 'Timezone updated successfully'
  },
  crm: {
    accessPermissions: 'Access Permissions',
    editAccess: {
      [ECrmFieldEditAccess.everyone]: 'Everyone can view and edit',
      [ECrmFieldEditAccess.adminOnly]: 'Only Admins can edit',
      [ECrmFieldEditAccess.viewOnly]: 'Read only'
    },
    crmReadOnlyPropertyHelpText:
      'This property is marked read-only in the CRM. Access permissions for such properties cannot be modified.',
    avomaMappedFieldAlwaysSynced:
      'If a CRM property is mapped to an Avoma property it is always synced back to CRM.',
    avomaReadOnlyPropertyHelpText:
      'This property is marked read-only in Avoma. Access permissions for such properties cannot be modified.',
    readOnly: 'Read only',
    makeReadOnlyConfirmText: {
      cannotBeMapped:
        "'Read-only' properties cannot be mapped to an Avoma property or smart topic.",
      willRemoveMapping:
        "Changing a property to 'Read-only' will remove the mapping, and no further updates will be sent to this CRM property.",
      doYouWishToContinue: 'Do you wish to continue?'
    },
    smartTopicExtraction: {
      label: 'Smart Topic Extraction',
      toggleLabel: 'Summarize Smart Topic notes in the deals dashboard',
      helpText:
        'When enabled, notes from various meetings and emails under each Smart Topic will be consolidated into a single summary. However, date sensitive Smart Topics like Action-items and Follow-up meetings will never be summarized.',
      updatedSuccessfully: enabled =>
        `Summarization ${enabled ? 'enabled' : 'disabled'}.`,
      confirmTitle: 'Enable Summarization ?',
      confirmText1:
        'Enabling summarization won’t automatically update historical data under AI methodology and Smart Topics in the deals dashboard.',
      confirmText2:
        'However, summarization will be triggered when there are newly extracted insights from meetings and emails related to a deal.'
    },
    emailSyncSettings: {
      label: 'Email Sync Settings',
      updatedSuccessfully: 'Email sync settings updated successfully.',
      updateFailed: 'Failed to update email sync settings',
      syncEmailFromTask: {
        cardTitle: 'Sync Emails from Tasks',
        toggleLabel: 'Sync emails from Task objects'
      },
      syncEmailFromEmails: {
        cardTitle: 'Sync Emails from Email Objects',
        toggleLabel: 'Sync emails from Email Message objects'
      }
    },
    changeAccessPermissionToReadOnly: 'Change access permission to Read Only?',
    saveChangesAndStartSyncing: 'Save Changes & Start Syncing',
    contacts: {
      contacts: 'Contacts',
      contactCreation: 'Contact creation',
      title: 'Configure contact settings',
      toggle: {
        title:
          'Automatically create CRM contacts from external meeting participants',
        description:
          'If enabled, Avoma will automatically create contacts in your CRM for external meeting participants if they don’t already exist in your CRM.'
      },
      conditions: {
        addConditions: 'Create a CRM contact if:',
        newRule: 'New rule'
      },
      createLeadIfNoAccount: {
        title: 'Automatically create a Lead if Account does not exist',
        description:
          "If enabled, Avoma will create a Lead if no Account or Lead exists for the participants' email"
      },
      skipPublicDomains: {
        title:
          'Create CRM contacts for email addresses from public email domains',
        description: (linkElement, isConditionsBelow = false) => (
          <React.Fragment>
            If enabled, Avoma <b>will</b> create contacts for email addresses
            that come from {linkElement} unless specified in the conditions{' '}
            {isConditionsBelow ? 'below' : 'above'}.
          </React.Fragment>
        ),
        publicDomains: 'public email domains'
      },
      disablePublicDomainsListed:
        'You can choose to disable CRM contact creation for email addresses with the public domains listed below',
      createContactsForAllParticipants:
        'Create contacts for participants for all external meetings?',
      noRulesSetWarning:
        "If you don't set rules to filter which meeting participants should become CRM contacts, we'll create contacts for all external meeting participants — which could result in unwanted contacts in your CRM.",
      successfullyUpdated: 'Successfully updated contact creation settings'
    },
    syncMeetingNotesToCrm: 'Sync meeting notes from Avoma to:',
    syncCallNotesToCrm: 'Sync call notes from Avoma to:',
    notesSyncSettings: {
      createCrmEvenWhenMeetingCreated: {
        title: (crmName, crmEventType) => (
          <React.Fragment>
            Create a{' '}
            <b>
              {crmName} {crmEventType}
            </b>{' '}
            object when a <b>meeting is created</b> in Avoma
          </React.Fragment>
        ),
        subtitle: (crmName, crmEventType) =>
          `Automatically creates a ${crmName} ${crmEventType} object when a meeting is created in Avoma, either via Calendar sync or as an impromptu external meeting.`
      },
      syncNotesToNotesObject: {
        title: crmName => (
          <React.Fragment>
            Which notes auto-sync to {crmName} <b>Note</b>?
          </React.Fragment>
        ),
        subtitle: crmName =>
          `Notes are saved into the ${crmName} Note object and attached to related records.`
      },
      syncNotesToEventObject: {
        title: (crmName, crmEventType) => (
          <React.Fragment>
            Which notes auto-sync to {crmName} <b>Event</b>?
          </React.Fragment>
        ),
        subtitle: crmProvider =>
          (crmProvider || '').toLowerCase() ===
          CrmProvider.hubspot.toLowerCase()
            ? 'Notes will be saved into Meeting object under Internal Notes field.'
            : `Notes are saved in the Event's description field and also appears in the Activity Timeline.`
      },
      syncNotesToTaskObject: {
        title: crmName => (
          <React.Fragment>
            Which notes auto-sync to {crmName} <b>Task</b>?
          </React.Fragment>
        ),
        subtitle:
          'Notes are added as comments inside task and also appear in the Activity Timeline.'
      },
      confirmationModal: {
        appliesToAllUsers: (
          <React.Fragment>
            This change will <b>apply to ALL users </b> in your organization who
            are present in CRM.
          </React.Fragment>
        ),
        createCrmEventTitleOn: (crmName, crmEventType) =>
          `Enable automatic creation of ${crmName} ${crmEventType} objects for all users?`,
        createCrmEventTitleOff: (crmName, crmEventType) =>
          `Disable automatic creation of ${crmName} ${crmEventType} objects for all users?`,
        createCrmEventBodyOn: (crmName, crmEventType) => (
          <React.Fragment>
            Avoma{' '}
            <b>
              will automatically create {`${crmName} ${crmEventType}`} objects
            </b>{' '}
            when a <b>new meeting is created in Avoma</b>.
          </React.Fragment>
        ),
        createCrmEventBodyOff: (crmName, crmEventType) => (
          <React.Fragment>
            Avoma{' '}
            <b>
              will no longer automatically create {`${crmName} ${crmEventType}`}{' '}
              objects
            </b>{' '}
            when a <b>new meeting is created in Avoma</b>.
          </React.Fragment>
        ),
        manualSyncFollowsCrmSettings: (
          <React.Fragment>
            Users can still <b>manually sync</b> these meetings, which will
            follow your <b>CRM note sync settings</b>.
          </React.Fragment>
        ),
        autoSyncMeetingNotesOn:
          'Enable automatic meeting note sync to CRM records for all users?',
        autoSyncMeetingNotesOff:
          'Disable automatic meeting note sync to CRM records for all users?',
        autoSyncCallNotesOn:
          'Enable automatic call note sync to CRM records for all users?',
        autoSyncCallNotesOff:
          'Disable automatic call note sync to CRM records for all users?',
        syncMeetingNotesToEventOn: (crmName, crmEventType) =>
          `Enable automatic meeting note sync to ${crmName} ${crmEventType} objects for all users?`,
        syncMeetingNotesToEventOff: (crmName, crmEventType) =>
          `Disable automatic meeting note sync to ${crmName} ${crmEventType} objects for all users?`,
        syncCallNotesToEventOn: (crmName, crmEventType) =>
          `Enable automatic call note sync to ${crmName} ${crmEventType} objects for all users?`,
        syncCallNotesToEventOff: (crmName, crmEventType) =>
          `Disable automatic call note sync to ${crmName} ${crmEventType} objects for all users?`,
        syncMeetingNotesToTaskOn: crmName =>
          `Enable automatic meeting note sync to ${crmName} Task objects for all users?`,
        syncMeetingNotesToTaskOff: crmName =>
          `Disable automatic meeting note sync to ${crmName} Task objects for all users?`,
        syncCallNotesToTaskOn: crmName =>
          `Enable automatic call note sync to ${crmName} Task objects for all users?`,
        syncCallNotesToTaskOff: crmName =>
          `Disable automatic call note sync to ${crmName} Task objects for all users?`,
        autoSyncEnabledBody: (
          <React.Fragment>
            Notes of the meetings which have CRM association and are <b>NOT</b>{' '}
            private will be <b>automatically synced to CRM</b>. Users can still
            manually sync meeting notes to associated CRM records.
          </React.Fragment>
        ),
        autoSyncDisabledBody: (
          <React.Fragment>
            <b>NONE</b> of the meeting notes will be <b>automatically synced</b>{' '}
            to associated CRM records. Users can still <b>manually sync</b>{' '}
            meeting notes to associated CRM records.
          </React.Fragment>
        ),
        lockedSettingsBody: (
          <React.Fragment>
            If any of the note sync settings are <b>locked</b>, users will not
            be able to make any changes on their own.
          </React.Fragment>
        )
      }
    }
  },
  automation: {
    plusNewRule: '+ New rule',
    plusAnd: '+ And'
  },
  settings: 'Settings',
  yourAccount: 'Your Account',
  organization: 'Organization',
  collapseSidebar: 'Collapse Settings sidebar',
  expandSidebar: 'Expand Settings sidebar'
});
